import React from 'react';
import BulkUpdateDialog from '@components-clean/core/bulk-update-dialog/bulk-update-dialog';
import { SelectedFieldValuesProps } from '@interfaces-clean/bulk-update.interface';
import { SelectedRowsProps } from '@interfaces-clean/observations.interface';
import { BulkUpdateSubjectActionsDto } from '@interfaces-clean/subject-monitoring/subject-actions';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import SubjectActionsApi from '@apis/bff-api/subject-monitoring/subject-actions';

interface BulkEditSmActionsDialogProps {
    studyRef: string;
    selectedRows: SelectedRowsProps[];
    onClose: () => void;
    onSubmit: () => void;
}

const SM_ACTION_FIELDS = [
    { field: 'completedDate', type: 'date', required: false, textLimit: 0 },
    { field: 'dueDate', type: 'date', required: false, textLimit: 0 },
    { field: 'ownerRef', type: 'smOwnerLov', required: false, textLimit: 0 },
    { field: 'requestedDate', type: 'date', required: true, textLimit: 0 },
    { field: 'resolution', type: 'text', required: false, textLimit: 3000 },
    { field: 'studyFunctionRef', type: 'responsibleFunctionLov', required: false, textLimit: 0 },
];

const BulkEditSmActionsDialog = ({ studyRef, selectedRows, onClose, onSubmit }: BulkEditSmActionsDialogProps) => {
    const { t } = useTranslation(['common']);
    const { t: tSm } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.bulkEditActionsDialog' });
    const { trigger: bulkUpdate } = SubjectActionsApi.useBulkUpdate(studyRef);

    const warningMessage = tSm('warningMessage', { count: selectedRows.length });

    const buildDto = (submittedValues: SelectedFieldValuesProps[]): BulkUpdateSubjectActionsDto => {
        const ownerField = submittedValues.find((f) => f.field === 'ownerRef');
        const dueDateField = submittedValues.find((f) => f.field === 'dueDate');
        const requestedDateField = submittedValues.find((f) => f.field === 'requestedDate');
        const completedDateField = submittedValues.find((f) => f.field === 'completedDate');
        const resolutionField = submittedValues.find((f) => f.field === 'resolution');
        const studyFunctionField = submittedValues.find((f) => f.field === 'studyFunctionRef');

        const dueDateValue = dueDateField?.value as string | undefined;
        const completedDateValue = completedDateField?.value as string | undefined;
        const requestedDateValue = requestedDateField?.value as string | undefined;

        return {
            actionIds: selectedRows.map((r) => Number(r.id)),
            ownerRef: ownerField != null ? (ownerField.value as string) || null : null,
            dueDate: dueDateValue ? (stringDateWithoutTimeFromLocal(dueDateValue) ?? dueDateValue) : null,
            clearDueDate: dueDateField != null && !dueDateValue,
            requestedDate: requestedDateValue
                ? (stringDateWithoutTimeFromLocal(requestedDateValue) ?? requestedDateValue)
                : null,
            completedDate: completedDateValue
                ? (stringDateWithoutTimeFromLocal(completedDateValue) ?? completedDateValue)
                : null,
            clearCompletedDate: completedDateField != null && !completedDateValue,
            resolution: resolutionField != null ? ((resolutionField.value as string) ?? null) : null,
            studyFunctionRef: studyFunctionField != null ? (studyFunctionField.value as string) || null : null,
        };
    };

    const onSubmitHandler = async (submittedValues: SelectedFieldValuesProps[]) => {
        try {
            const success = await bulkUpdate(buildDto(submittedValues));
            if (success) {
                ShowSuccess(t('actionsSuccessfulToast'));
                onSubmit();
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
        }
    };

    return (
        <BulkUpdateDialog
            studyRef={studyRef}
            dialogTitle={tSm('title')}
            warningMessage={warningMessage}
            fields={SM_ACTION_FIELDS}
            onClose={onClose}
            onSubmit={onSubmitHandler}
            unsavedChangesArea="subject-monitoring-toolkit"
        />
    );
};

export default BulkEditSmActionsDialog;
